import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useBackNavigate } from '@/shared/hooks/use-back-navigate';
import {
  Button,
  Close,
  Edit,
  Select,
  Input,
  Modal,
  PageContainer,
} from '@react-apps/design-system';
import {
  CONTENT_RELATIONS,
  type ContentItem,
  type ContentItemDetail,
  type ContentLinkDetail,
  type ContentRelation,
} from '../../domain/entities/content.entity';
import { useContentItem } from '../hooks/use-content-items';
import {
  usePublishContentItem,
  useArchiveContentItem,
  useDeleteContentItem,
  useDeleteQuestion,
  useUpdateContentItemBody,
} from '../hooks/use-content-items';
import {
  useSetContentClassification,
  useLinkContent,
  useUnlinkContent,
} from '../hooks/use-content-relations';
import { useCanLinkContent } from '../hooks/use-content-permissions';
import {
  CONTENT_RELATION_LABELS,
  contentTypeLabel,
  ContentStatusBadge,
  ErrorState,
  Mono,
  SectionCard,
  contentDisplayName,
  getApiErrorMessage,
  notifyDone,
  notifyFailed,
  relationHeading,
} from '../display';
import { readQuestionBody } from '../exam-body';
import { isAnnulled } from '../answer-key';
import { ConfirmModal } from '../components/ConfirmModal';
import { BodyForm } from '../components/BodyForm';
import { BodyView } from '../components/BodyView';
import { SubjectSearch } from '../components/SubjectSearch';
import { EntityLink } from '@/shared/components/entity-link';
import { CopyButton } from '@/shared/components/copy-button';
import { IconButton } from '@/shared/components/icon-button';

export function ContentItemDetailPage() {
  const { contentId } = useParams<{ contentId: string }>();
  const { data, isLoading, isError, error } = useContentItem(contentId);

  const publish = usePublishContentItem();
  const archive = useArchiveContentItem();
  const removeItem = useDeleteContentItem();
  const removeQuestion = useDeleteQuestion();
  const goBack = useBackNavigate('/content');
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  if (isLoading) {
    return (
      <PageContainer maxWidth="5xl" spacing="none">
        <p className="text-(--color-text-secondary) text-sm">Carregando…</p>
      </PageContainer>
    );
  }

  if (isError || !data) {
    return (
      <PageContainer maxWidth="5xl" spacing="sm">
        <BackLink />
        <ErrorState
          message={getApiErrorMessage(error, 'Conteúdo não encontrado.')}
        />
      </PageContainer>
    );
  }

  const { item } = data;
  const isQuestion = item.type === 'QUESTION';
  // Questão sai pelo caminho que ajusta a prova — ver `useDeleteQuestion`.
  const remove = isQuestion ? removeQuestion : removeItem;
  const actionError = publish.error || archive.error || remove.error;
  // O título já diz prova/simulado; o rótulo embaixo sai sem o `Prova`.
  const typeLabel = contentTypeLabel(item.type, examKindOf(item));

  return (
    <PageContainer maxWidth="5xl">
      <BackLink />

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-(--color-text-primary) text-xl font-semibold">
              {typeLabel} <Mono>{item.id}</Mono>
            </h1>
            <CopyButton text={item.id} label="Copiar id" icon />
          </div>
          {data.label && (
            <p className="text-(--color-text-primary) text-sm">{data.label}</p>
          )}
          <div className="flex items-center gap-2">
            <ContentStatusBadge status={item.status} />
            <span className="text-(--color-text-secondary) text-xs">
              versão {item.version}
            </span>
            {item.deletedAt && (
              <span className="text-xs font-medium text-red-500">Excluído</span>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            disabled={
              item.status === 'PUBLISHED' ||
              !!item.deletedAt ||
              publish.isPending
            }
            onClick={() => publish.mutate(item.id)}
          >
            Publicar
          </Button>
          <Button
            variant="secondary"
            disabled={
              item.status === 'ARCHIVED' ||
              !!item.deletedAt ||
              archive.isPending
            }
            onClick={() => archive.mutate(item.id)}
          >
            Arquivar
          </Button>
          <Button
            variant="danger"
            disabled={!!item.deletedAt}
            onClick={() => setIsDeleteOpen(true)}
          >
            Excluir
          </Button>
        </div>
      </div>

      {actionError && (
        <p className="text-sm text-red-600">
          {getApiErrorMessage(actionError, 'Falha na ação')}
        </p>
      )}

      <BodySection
        item={item}
        imageUrlsByFileId={data.imageUrlsByFileId ?? {}}
      />

      {/* Prova não se classifica por assunto — por enquanto, só a questão. */}
      {item.type !== 'EXAM' && (
        <SubjectSection contentId={item.id} data={data} />
      )}
      <LinksSection item={item} data={data} />

      <ConfirmModal
        open={isDeleteOpen}
        onClose={() => {
          setIsDeleteOpen(false);
          remove.reset();
        }}
        onConfirm={() => {
          if (!isQuestion) {
            removeItem.mutate(item.id, {
              onSuccess: () => setIsDeleteOpen(false),
            });
            return;
          }
          removeQuestion.mutate(item.id, {
            onSuccess: (result) => {
              setIsDeleteOpen(false);
              if (result.mode === 'HARD_DELETED') {
                // Apagada de vez: esta ficha não existe mais.
                notifyDone('Questão excluída');
                goBack();
              } else {
                notifyDone('Questão saiu da prova (registro preservado)');
              }
            },
            // Troca o toast cru pela frase traduzida — ex.:
            // `QUESTION_REFERENCED`, rascunho que algum simulado aponta.
            onError: (err) => notifyFailed(err, 'A questão não foi excluída'),
          });
        }}
        title={isQuestion ? 'Excluir questão' : 'Excluir conteúdo'}
        // Mesmas frases do preview da prova (`QuestionPreviewCard`): o
        // caminho é o mesmo, então o efeito descrito também.
        description={
          !isQuestion
            ? 'O item deixa de aparecer nas listagens e em tudo que o usa. O registro fica guardado, mas não dá para desfazer por aqui.'
            : item.status === 'DRAFT'
              ? 'A questão está em rascunho: será apagada de vez, e a numeração da prova é ajustada. Se algum simulado aponta para ela, a exclusão é recusada.'
              : 'A questão já foi publicada: ela sai da prova — e de todo simulado que aponta para ela — e a numeração é ajustada. O registro é preservado, porque pode estar em atividades de aluno.'
        }
        confirmLabel="Excluir"
        loading={remove.isPending}
        error={remove.error}
      />
    </PageContainer>
  );
}

function BackLink() {
  const goBack = useBackNavigate('/content');
  return (
    <button
      type="button"
      onClick={goBack}
      className="text-(--color-text-secondary) hover:text-(--color-text-primary) text-sm"
    >
      ← Voltar para Conteúdo
    </button>
  );
}

// --- Body (editável — `type` é imutável) ------------------------------------

function BodySection({
  item,
  imageUrlsByFileId,
}: {
  item: ContentItem;
  imageUrlsByFileId: Record<string, string>;
}) {
  const [editing, setEditing] = useState(false);
  const update = useUpdateContentItemBody(item.id);

  const startEdit = () => {
    update.reset();
    setEditing(true);
  };

  const cancel = () => {
    update.reset();
    setEditing(false);
  };

  const typeLabel = contentTypeLabel(item.type, examKindOf(item));
  return (
    <SectionCard
      title={typeLabel}
      action={
        !editing && (
          <IconButton
            label={`Editar ${typeLabel.toLowerCase()}`}
            icon={<Edit size="sm" />}
            onClick={startEdit}
            disabled={!!item.deletedAt}
          />
        )
      }
    >
      {editing ? (
        <BodyForm
          type={item.type}
          body={item.body}
          imageUrlsByFileId={imageUrlsByFileId}
          pending={update.isPending}
          error={update.error}
          /* Sem isto, salvar o texto de uma questão anulada a desanularia: o
             form monta o body inteiro, e o que ele não recebe ele não devolve.
             Anular/desanular em si é operação da tela da prova (é lá que o
             gabarito está) — aqui o valor só atravessa. */
          annulled={isAnnulled(readQuestionBody(item.body))}
          onSubmit={(result) =>
            update.mutate(
              { body: result.body, images: result.images },
              { onSuccess: () => setEditing(false) },
            )
          }
          onCancel={cancel}
        />
      ) : (
        <BodyView item={item} imageUrlsByFileId={imageUrlsByFileId} />
      )}
    </SectionCard>
  );
}

function examKindOf(item: ContentItem) {
  if (item.type !== 'EXAM') return null;
  const kind = (item.body as { examKind?: unknown }).examKind;
  return kind === 'MOCK_EXAM' || kind === 'REAL_EXAM' ? kind : null;
}

// --- Assunto (conteúdo ↔ subject) -----------------------------------------

/**
 * A ficha tem **um** assunto. O back ainda aceita `SECONDARY` e o dado legado
 * fica onde está, mas nada lê secundária: trocar o assunto grava pelo `PUT
 * subjects` (o mesmo do preview da prova), que repõe a classificação inteira
 * numa transação — uma secundária legada sai junto.
 */
function SubjectSection({
  contentId,
  data,
}: {
  contentId: string;
  data: ContentItemDetail;
}) {
  const save = useSetContentClassification(contentId);
  const current =
    data.classifications.find((c) => c.relation === 'PRIMARY') ??
    data.classifications[0];
  // Assunto removido não mostra o id: o UUID cru é o que a ficha deixou de ter.
  const currentName = current?.subjectName ?? null;

  return (
    <SectionCard title="Assunto">
      {current ? (
        <div className="flex items-center justify-between gap-3">
          <span className="text-(--color-text-primary) text-sm">
            {currentName ?? 'Assunto removido'}
          </span>
          <IconButton
            label={
              currentName
                ? `Remover o assunto ${currentName}`
                : 'Remover o assunto'
            }
            icon={<Close size="sm" />}
            disabled={save.isPending}
            onClick={() => save.mutate({ subjectIds: [] })}
          />
        </div>
      ) : (
        <p className="text-(--color-text-secondary) text-sm">Sem assunto.</p>
      )}

      <SubjectSearch
        label={current ? 'Trocar assunto' : 'Definir assunto'}
        excludeId={current?.subjectId}
        disabled={save.isPending}
        onPick={(subject) => save.mutate({ subjectIds: [subject.id] })}
      />

      {save.error != null && (
        <p className="text-sm text-red-600" role="alert">
          {getApiErrorMessage(save.error, 'Falha ao gravar o assunto')}
        </p>
      )}
    </SectionCard>
  );
}

// --- Relações (conteúdo ↔ conteúdo) ----------------------------------------

type LinkRow = { link: ContentLinkDetail; side: 'source' | 'target' };

function LinksSection({
  item,
  data,
}: {
  item: ContentItem;
  data: ContentItemDetail;
}) {
  const canLink = useCanLinkContent();
  const unlink = useUnlinkContent(item.id);

  // Um bloco por (relação, lado), na ordem do vocabulário: o título já diz a
  // direção, então "saindo/chegando" deixa de ser seção.
  const groups = CONTENT_RELATIONS.flatMap((relation) =>
    (['source', 'target'] as const).map((side) => ({
      relation,
      side,
      rows: (side === 'source' ? data.outgoingLinks : data.incomingLinks)
        .filter((l) => l.relation === relation)
        .map((link): LinkRow => ({ link, side })),
    })),
  ).filter((g) => g.rows.length > 0);

  const [linking, setLinking] = useState(false);
  const isExam = item.type === 'EXAM';
  const hasQuestions = data.outgoingLinks.some(
    (l) => l.relation === 'CONTAINS',
  );

  return (
    <SectionCard
      title="Relações"
      action={
        canLink && (
          <Button variant="secondary" onClick={() => setLinking(true)}>
            Adicionar nova relação
          </Button>
        )
      }
    >
      {canLink && (
        <LinkModal
          contentId={item.id}
          open={linking}
          onClose={() => setLinking(false)}
        />
      )}

      {isExam && !hasQuestions && (
        <p className="text-(--color-text-secondary) text-sm">
          Nenhuma questão nesta prova.
        </p>
      )}
      {!isExam && groups.length === 0 && (
        <p className="text-(--color-text-secondary) text-sm">
          Nenhuma relação.
        </p>
      )}

      {unlink.error != null && (
        <p className="text-sm text-red-600" role="alert">
          {getApiErrorMessage(unlink.error, 'Falha ao desvincular')}
        </p>
      )}

      <div className="space-y-4">
        {groups.map((group) => (
          <div key={`${group.relation}-${group.side}`}>
            <h3 className="text-(--color-text-secondary) mb-2 text-xs font-medium uppercase tracking-wide">
              {relationHeading(group.relation, group.side, item.type)} (
              {group.rows.length})
            </h3>
            <ul className="divide-(--color-border) divide-y">
              {group.rows.map(({ link, side }) => {
                const otherId =
                  side === 'source'
                    ? link.targetContentId
                    : link.sourceContentId;
                const name = link.counterpart
                  ? contentDisplayName({
                      type: link.counterpart.type,
                      id: otherId,
                      label: link.counterpart.label,
                      examKind: link.counterpart.examKind,
                      position: side === 'source' ? link.position : null,
                    })
                  : otherId;
                return (
                  <li
                    key={link.id}
                    className="flex items-center justify-between gap-3 py-2"
                  >
                    <span className="text-sm">
                      <EntityLink kind="contentItem" id={otherId}>
                        {name}
                      </EntityLink>
                    </span>
                    {side === 'source' && (
                      <IconButton
                        label={
                          isExam && link.relation === 'CONTAINS'
                            ? `Tirar ${name} da prova`
                            : `Desvincular ${name}`
                        }
                        icon={<Close size="sm" />}
                        // Sem confirmação: o clique duplo dispararia duas vezes.
                        disabled={unlink.isPending}
                        onClick={() =>
                          unlink.mutate({
                            targetContentId: link.targetContentId,
                            relation: link.relation,
                          })
                        }
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

/** Relacionar por id — só para quem a rota aceita (`useCanLinkContent`). */
function LinkModal({
  contentId,
  open,
  onClose,
}: {
  contentId: string;
  open: boolean;
  onClose: () => void;
}) {
  const [targetId, setTargetId] = useState('');
  const [relation, setRelation] = useState<ContentRelation>(
    CONTENT_RELATIONS[0],
  );
  const [position, setPosition] = useState('');
  const link = useLinkContent(contentId);

  const close = () => {
    link.reset();
    setTargetId('');
    setPosition('');
    onClose();
  };

  const canAdd =
    targetId.trim() !== '' && targetId.trim() !== contentId && !link.isPending;

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canAdd) return;
    link.mutate(
      {
        targetContentId: targetId.trim(),
        relation,
        ...(position ? { position: Number(position) } : {}),
      },
      { onSuccess: close },
    );
  };

  return (
    <Modal open={open} onClose={close} title="Adicionar nova relação">
      <form onSubmit={handleAdd} className="space-y-4">
        <Input
          label="Id do conteúdo"
          value={targetId}
          onChange={(e) => setTargetId(e.target.value)}
          placeholder="id do conteúdo a relacionar…"
        />
        <Select
          label="Relação"
          value={relation}
          onChange={(e) => setRelation(e.target.value as ContentRelation)}
          options={CONTENT_RELATIONS.map((r) => ({
            value: r,
            label: CONTENT_RELATION_LABELS[r],
          }))}
        />
        <Input
          label="Posição (opcional)"
          type="number"
          value={position}
          onChange={(e) => setPosition(e.target.value)}
        />
        {link.error && (
          <p className="text-sm text-red-600">
            {getApiErrorMessage(link.error, 'Falha ao relacionar')}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="ghost" onClick={close}>
            Cancelar
          </Button>
          <Button type="submit" disabled={!canAdd}>
            {link.isPending ? 'Relacionando…' : 'Relacionar'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
